import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  Typography,
  Tooltip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Divider,
  Paper,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import LinkTargetSelector from './LinkTargetSelector';
import BannerLibrary from './BannerLibrary';

type BannerLinkType = 'category' | 'product' | 'brand' | 'brands-page' | 'store' | 'custom';

interface BannerItemFormData {
  name: string;
  redirectUrl: string;
  isActive: boolean;
  image?: File | null;
  linkType: BannerLinkType | null;
  linkedEntityId: string | null;
}

interface BannerItem {
  id: string;
  name: string;
  /** S3 key (e.g. "banners/abc123.jpg") */
  image?: string;
  imageUrl: string;
  redirectUrl: string;
  isActive: boolean;
  linkType?: BannerLinkType | null;
  linkedEntityId?: string | null;
}

/** Data returned when saving in draft mode */
interface BannerItemDraftSaveData {
  id: string;
  name: string;
  image: string;
  imageUrl: string;
  redirectUrl: string | null;
  linkType: BannerLinkType | null;
  linkedEntityId: string | null;
  isActive: boolean;
  isDefault: boolean;
}

interface BannerItemFormDialogProps {
  open: boolean;
  onClose: () => void;
  bannerId: string;
  bannerName: string;
  item?: BannerItem | null;
  onSuccess: () => void;
  /** When provided, saves go through draft instead of live API */
  onDraftSave?: (data: BannerItemDraftSaveData) => Promise<void>;
}

// No store-visibility step: one layout for every store, and a banner always shows
// (it still clicks through when its linked product/category is out of stock).
const STEPS = ['Image', 'Link Target', 'Details'];

/**
 * Banner Item Form Dialog
 *
 * A modern, step-based dialog for creating and editing banner items.
 * Features:
 * - Step-by-step wizard interface
 * - Image upload with drag-and-drop preview
 * - Smart link target selector (category/product/brand/custom)
 * - Name and active toggle
 * - Form validation with clear error messages
 */
const BannerItemFormDialog: React.FC<BannerItemFormDialogProps> = ({
  open,
  onClose,
  bannerId,
  bannerName,
  item,
  onSuccess,
  onDraftSave,
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<BannerItemFormData>({
    name: '',
    redirectUrl: '',
    isActive: true,
    image: null,
    linkType: null,
    linkedEntityId: null,
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkType, setLinkType] = useState<string>('custom');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [selectedLibraryImage, setSelectedLibraryImage] = useState<string | null>(null);
  const [selectedLibraryImageKey, setSelectedLibraryImageKey] = useState<string | null>(null);
  const notify = useNotify();

  const isEditMode = !!item;

  // Initialize form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        redirectUrl: item.redirectUrl || '',
        isActive: item.isActive,
        image: null,
        linkType: (item.linkType as BannerLinkType) || null,
        linkedEntityId: item.linkedEntityId || null,
      });
      setImagePreview(item.imageUrl);
      setActiveStep(0);
    } else {
      setFormData({
        name: '',
        redirectUrl: '',
        isActive: true,
        image: null,
        linkType: null,
        linkedEntityId: null,
      });
      setImagePreview(null);
      setActiveStep(0);
    }
    setSelectedLibraryImage(null);
    setSelectedLibraryImageKey(null);
    setError(null);
  }, [item, open]);

  const handleInputChange = (field: keyof BannerItemFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, isActive: e.target.checked });
  };

  const handleLinkChange = (url: string, type: string, linkedItem?: { id: string; name: string }) => {
    setFormData({
      ...formData,
      redirectUrl: url,
      linkType: (type as BannerLinkType) || null,
      linkedEntityId: linkedItem?.id || null,
    });
    setLinkType(type);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        setError(`Image file is too large (${sizeMB}MB). Please choose an image smaller than 5MB.`);
        return;
      }

      setFormData({ ...formData, image: file });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file is too large. Please choose an image smaller than 5MB.');
        return;
      }

      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Image
        if (!isEditMode && !formData.image && !imagePreview) {
          setError('Please upload a banner image');
          return false;
        }
        break;
      case 1: // Link Target
        // Link is optional, so no validation needed
        break;
      case 2: // Details
        if (!formData.name.trim()) {
          setError('Please enter a name for this banner');
          return false;
        }
        if (formData.name.trim().length < 3) {
          setError('Banner name must be at least 3 characters');
          return false;
        }
        break;
    }
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setLoading(true);
    setError(null);

    try {
      // ---------- Draft mode: save to draft state, not live DB ----------
      if (onDraftSave) {
        let imageKey: string;
        let imageUrl: string;

        if (formData.image) {
          // New file selected → upload via draft image endpoint
          const uploadForm = new FormData();
          uploadForm.append('image', formData.image);

          const uploadRes = await authInstance.post('/admin/home-layout/upload-banner-image', uploadForm, {
            headers: { 'Content-Type': undefined },
          });
          imageKey = uploadRes.data.image;
          imageUrl = uploadRes.data.imageUrl;
        } else if (selectedLibraryImage && selectedLibraryImageKey) {
          // Library image selected → reuse existing S3 key
          imageKey = selectedLibraryImageKey;
          imageUrl = selectedLibraryImage;
        } else if (isEditMode && item) {
          // Editing without changing image → keep existing
          imageKey = item.image || '';
          imageUrl = item.imageUrl;
        } else {
          throw new Error('Please upload or select a banner image');
        }

        const draftData: BannerItemDraftSaveData = {
          id: isEditMode && item ? item.id : `draft_${crypto.randomUUID()}`,
          name: formData.name,
          image: imageKey,
          imageUrl,
          redirectUrl: formData.redirectUrl?.trim() || null,
          linkType: formData.linkType,
          linkedEntityId: formData.linkedEntityId,
          isActive: formData.isActive,
          isDefault: false,
        };

        await onDraftSave(draftData);

        notify(
          isEditMode
            ? `Banner "${formData.name}" updated in draft`
            : `Banner "${formData.name}" added to draft`,
          { type: 'success' }
        );
        onClose();
        return;
      }

      // ---------- Legacy mode: save directly to live DB ----------
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      // Only send redirectUrl if it has a value (backend requires valid URL or null)
      if (formData.redirectUrl && formData.redirectUrl.trim()) {
        formDataToSend.append('redirectUrl', formData.redirectUrl);
      }
      // If empty, don't send - backend keeps existing value or uses null
      formDataToSend.append('isActive', String(formData.isActive));

      // Link type and linked entity
      if (formData.linkType) {
        formDataToSend.append('linkType', formData.linkType);
      }
      if (formData.linkedEntityId) {
        formDataToSend.append('linkedEntityId', formData.linkedEntityId);
      }

      // Only send bannerId when creating (not when editing)
      if (!isEditMode) {
        formDataToSend.append('bannerId', bannerId);
      }

      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      const apiUrl = isEditMode
        ? `/admin/banner-items/${item.id}`
        : '/admin/banner-items';

      if (isEditMode) {
        await authInstance.patch(apiUrl, formDataToSend);
      } else {
        await authInstance.post(apiUrl, formDataToSend);
      }

      notify(
        isEditMode
          ? `Banner "${formData.name}" updated successfully`
          : `Banner "${formData.name}" created successfully`,
        { type: 'success' }
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to save banner item. Please check your connection and try again.';
      setError(errorMessage);
      notify(errorMessage, { type: 'error' });
      console.error('Error saving banner item:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Image Step
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Banner Image
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload a new image or select from your existing banner library. Recommended size: 1200x400px (3:1 ratio).
            </Typography>

            {/* Library Selection Button */}
            {!imagePreview && (
              <Button
                variant="outlined"
                startIcon={<PhotoLibraryIcon />}
                onClick={() => setLibraryOpen(true)}
                sx={{ mb: 2 }}
                fullWidth
              >
                Choose from Library
              </Button>
            )}

            {/* Image Upload Area */}
            <Paper
              variant="outlined"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                p: 4,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: imagePreview ? 'success.main' : 'divider',
                bgcolor: imagePreview
                  ? alpha(theme.palette.success.main, 0.05)
                  : alpha(theme.palette.action.hover, 0.5),
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              {imagePreview ? (
                <Box>
                  <Card sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
                    <CardMedia
                      component="img"
                      image={imagePreview}
                      alt="Banner preview"
                      sx={{ height: 150, objectFit: 'cover' }}
                    />
                  </Card>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                    >
                      Change Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleImageChange}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PhotoLibraryIcon />}
                      onClick={() => setLibraryOpen(true)}
                    >
                      From Library
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box component="label" sx={{ cursor: 'pointer', display: 'block' }}>
                  <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Drop image here or click to upload
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports JPG, PNG, GIF, WebP (Max 5MB)
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                </Box>
              )}
            </Paper>

            {/* Banner Library Dialog */}
            <BannerLibrary
              open={libraryOpen}
              onClose={() => setLibraryOpen(false)}
              onSelect={(imageUrl, imageKey) => {
                setImagePreview(imageUrl);
                setSelectedLibraryImage(imageUrl);
                setSelectedLibraryImageKey(imageKey || null);
                // Clear the file input since we're using a library image
                setFormData({ ...formData, image: null });
              }}
              selectedImageUrl={selectedLibraryImage}
            />
          </Box>
        );

      case 1: // Link Target Step
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Link Target
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Where should customers go when they tap this banner? (Optional)
            </Typography>

            <LinkTargetSelector
              value={formData.redirectUrl}
              onChange={handleLinkChange}
              error={error || undefined}
            />
          </Box>
        );

      case 2: // Details Step
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Banner Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Give this banner a name and set its visibility.
            </Typography>

            <TextField
              label="Banner Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              fullWidth
              required
              placeholder="e.g., Summer Sale 2026, New Arrivals"
              helperText="Internal name for reference (not shown to customers)"
              sx={{ mb: 3 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Visibility
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Paper
                onClick={() => setFormData({ ...formData, isActive: true })}
                sx={{
                  flex: 1,
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: formData.isActive ? 'success.main' : 'divider',
                  borderRadius: 2,
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  bgcolor: formData.isActive ? alpha(theme.palette.success.main, 0.1) : 'background.paper',
                  '&:hover': {
                    borderColor: 'success.main',
                  },
                }}
              >
                {formData.isActive && (
                  <CheckCircleIcon sx={{ color: 'success.main', position: 'absolute', top: 8, right: 8 }} />
                )}
                <Typography variant="subtitle1" fontWeight={600} color={formData.isActive ? 'success.main' : 'text.primary'}>
                  Active
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Visible on mobile app
                </Typography>
              </Paper>

              <Paper
                onClick={() => setFormData({ ...formData, isActive: false })}
                sx={{
                  flex: 1,
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: !formData.isActive ? 'warning.main' : 'divider',
                  borderRadius: 2,
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  bgcolor: !formData.isActive ? alpha(theme.palette.warning.main, 0.1) : 'background.paper',
                  '&:hover': {
                    borderColor: 'warning.main',
                  },
                }}
              >
                {!formData.isActive && (
                  <CheckCircleIcon sx={{ color: 'warning.main', position: 'absolute', top: 8, right: 8 }} />
                )}
                <Typography variant="subtitle1" fontWeight={600} color={!formData.isActive ? 'warning.main' : 'text.primary'}>
                  Inactive
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hidden from customers
                </Typography>
              </Paper>
            </Stack>

            {/* Summary */}
            <Alert severity="info" sx={{ mt: 4 }} icon={<SettingsIcon />}>
              <Typography variant="body2">
                <strong>Summary:</strong> {formData.name || 'Unnamed banner'} will be{' '}
                {formData.isActive ? 'visible' : 'hidden'}
                {formData.redirectUrl ? (
                  <>, linking to <Chip label={linkType} size="small" sx={{ mx: 0.5 }} /></>
                ) : (
                  ', with no link'
                )}
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            {isEditMode ? 'Edit Banner Item' : `Add to ${bannerName || 'Banner'}`}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label, index) => (
            <Step key={label} completed={activeStep > index}>
              <StepLabel>
                <Typography variant="body2" fontWeight={activeStep === index ? 600 : 400}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button onClick={handleNext} variant="contained">
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{
              background: 'linear-gradient(135deg, #ff002b 0%, #ff4757 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e6002a 0%, #e6414d 100%)',
              },
            }}
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Banner' : 'Create Banner'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BannerItemFormDialog;
